import {ConsumerInformation, EOrderStatus, Order, orderStore} from '../../../../../redux/entities/inventory/order';
import React, {useCallback, useMemo, useRef, useState} from 'react';
import {connect} from 'react-redux';
import gridStyles from '../../../../GridStyles.module.scss';
import {bindActionCreators, Dispatch} from 'redux';
import {ConfirmationDialog} from '../../../../../components/util/ConfirmationDialog/ConfirmationDialog';
import IconButton from '../../../../../components/util/widgets/IconButton/IconButton';
import { Button, Paper } from '@mantine/core';
import {Navigate, useNavigate, useParams} from 'react-router-dom';
import {format, utcToZonedTime} from 'date-fns-tz';
import {parseISO} from 'date-fns';
import {CommonState} from '../../../../../redux';
import {localTz, localTzFriendlyFormat} from '../../../../../util/date';
import {formatCentsToUSD, propertyOf} from '../../../../../util';
import {isNullOrWhitespace} from '../../../../../util/string';
import {AppTheme} from '../../../../../appTheme';
import {AxiosError} from 'axios/index';
import {getErrorResponseMessage} from '../../../../../util/http';
import StyledMantineTable, { type MRT_ColumnDef } from '../../../../../components/util/StyledMantineTable/StyledMantineTable';
import useStandardPrint from '../../../../../hooks/useStandardPrint/useStandardPrint';
import DepositModal from './DepositModal';
import {makeOrder} from "../../../../../redux/factory/inventory/order";
import { RoutePaths } from "../../../../../router/RoutePaths";
import {RoleProtectedComponent} from "../../../../../components/util/RoleProtectedRoute/RoleProtectedComponent";
import AdministratorComponent from "../../../../../components/util/RoleGatedComponent/AdministratorComponent";
import AuditLogLoader from "../../../../private/AuditLog/AuditLogLoader";

interface TFilterDD {value: 'all' | 'unapproved' | 'approved' | 'shipped' | 'cancelled' | 'notCancelled'; label: string;}

const orderFilterDropdownOptions: TFilterDD[] = [
  {value: 'all', label: 'All Orders'},
  {value: 'unapproved', label: 'Unapproved Orders'},
  {value: 'approved', label: 'Approved Orders'},
  {value: 'shipped', label: 'Shipped Orders'},
  {value: 'cancelled', label: 'Cancelled Orders'},
  {value: 'notCancelled', label: 'Do Not Show Cancelled Orders'}
];

const defaultFilterValue = orderFilterDropdownOptions[5];

type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

function DepositGrid(props: Props) {
  const {deposits, getOrderById, actions: {updateOrderStatus, cancelOrder, deleteOrder}} = props;
  const [isDeleting, setIsDeleting] = useState('');
  const {id : paramsId} = useParams();
  const [editingOrderData, setEditingOrderData] = useState<{editing: boolean; id: string}>(
    {editing: false, id: paramsId ?? ''});
  const [cancellingOrder, setCancellingOrder] = useState<Order | null>(null);
  const [deletingOrder, setDeletingOrder] = useState<Order | null>(null);
  const [creatingOrder, setCreatingOrder] = useState<Order | null>(null);
  const [viewingOrderId, setViewingOrderId] = useState<string>('');
  // Local audit log drawer state
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditEntityId, setAuditEntityId] = useState<string | undefined>(undefined);
  const [orderFilter, setOrderFilter] = useState<TFilterDD>(defaultFilterValue);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const printRef = useRef<HTMLDivElement | null>(null);
  const handleprint = useStandardPrint(printRef);
  const navigate = useNavigate();

  const getFieldName = propertyOf<Order>;
  const getConsumerInfoName = (value: keyof ConsumerInformation) => `${getFieldName('consumerInformation')}.${value}`;

  // Open the Deposit modal automatically when an :id param is present
  React.useEffect(() => {
    if (paramsId && !editingOrderData.editing) {
      const existing = getOrderById(paramsId);
      if (existing && !existing.isWithdrawal) {
        setEditingOrderData({editing: true, id: paramsId});
      }
    }
  }, [paramsId, getOrderById, editingOrderData.editing]);

  const processStatusSubmit = async (order: Order) => {
    try {
      await updateOrderStatus(order.id, order.orderStatus + 1);
    } catch (e: AxiosError | any) {
      setErrorMessage(getErrorResponseMessage(e));
    }
  };

  const renderDeletionModal = () => {
    if (!deletingOrder) return null;
    return (
      <ConfirmationDialog
        open={true}
        onAccept={async () => {
          await deleteOrder(deletingOrder.id);
          setDeletingOrder(null);
        }}
        onDecline={async () => {
          setDeletingOrder(null);
        }}
        prompt={`Are you sure you would like to permenantly delete ${deletingOrder.organization}'s
         order? This CANNOT be undone, it is recommended to leave orders cancelled.`}
        positiveText={`Delete order`}
        negativeText={'Cancel'}
        positiveVariant={'warning'}
        negativeVariant={'danger'}
      />
    );
  };

  const renderCancellationModal = () => {
    if (!cancellingOrder) return null;
    return (
      <ConfirmationDialog
        open={true}
        onAccept={async () => {
          await cancelOrder(cancellingOrder.id);
          setCancellingOrder(null);
        }}
        onDecline={async () => {
          setCancellingOrder(null);
        }}
        prompt={`Are you sure you would like to cancel ${cancellingOrder.organization}'s
         order? This can be undone.`}
        positiveText={`Mark as cancelled`}
        negativeText={'Cancel'}
        positiveVariant={'success'}
        negativeVariant={'danger'}
      />
    );
  };

  const renderRedirect = () => {
    if (redirectUrl.length !== 0) {
      return <Navigate to={redirectUrl} />;
    }
    return null;
  };


  const getDisabledToolTipText = (order: Order) => {
    if (order.orderStatus === EOrderStatus.Cancelled)
      return 'Order is cancelled';
    if (order.orderStatus === EOrderStatus.Unapproved)
      return 'Order needs approval first';
    if (order.orderStatus === EOrderStatus.Approved)
      return 'Order has already been approved';
    return 'Order has already been shipped';
  };

  const actionsFormatter = useCallback((cell: any, order: Order) => {
    const approveEnabled = order.orderStatus === EOrderStatus.Unapproved;
    const shipEnabled = order.orderStatus === EOrderStatus.Approved;
    return (
      <div className={gridStyles['table-actions-wrapper']}>
        {order.orderStatus !== EOrderStatus.Cancelled ? (
          <>
            <div>
              <AdministratorComponent>
                <IconButton
                  icon={'history'}
                  styles={{color: AppTheme.colors.themePurple, marginLeft: '.5rem'}}
                  size={'1x'}
                  iconToolTipText={'History'}
                  onClick={() => { setAuditEntityId(order.id); setAuditOpen(true); }}
                />
              </AdministratorComponent>
            </div>
            <div>
                <IconButton
                  icon={'eye'}
                  styles={{color: AppTheme.colors.themePurple, marginLeft: '.5rem'}}
                  size={'1x'}
                  iconToolTipText={'View'}
                  onClick={() => setViewingOrderId(order.id)}
                />
            </div>
              <div>
                <IconButton
                  icon={'pen'}
                  styles={{color: AppTheme.colors.themePurple, marginLeft: '.5rem'}}
                  size={'1x'}
                  iconToolTipText={'Edit'}
                  onClick={() => setEditingOrderData({editing: true, id: order.id})}
                />
              </div>
              <div>
                <IconButton
                  icon={'x'}
                  styles={{color: AppTheme.colors.danger, marginLeft: '.5rem'}}
                  size={'1x'}
                  iconToolTipText={'Cancel'}
                  onClick={() => setCancellingOrder(order)}
                />
              </div>
          </> ) : (
          <>
            <div>
              <AdministratorComponent>
                <IconButton
                  icon={'history'}
                  styles={{color: AppTheme.colors.themePurple, marginLeft: '.5rem'}}
                  size={'1x'}
                  iconToolTipText={'History'}
                  onClick={() => { setAuditEntityId(order.id); setAuditOpen(true); }}
                />
              </AdministratorComponent>
            </div>
            <div>
              <IconButton
                icon={'eye'}
                styles={{color: AppTheme.colors.themePurple, marginLeft: '.5rem'}}
                size={'1x'}
                iconToolTipText={'View'}
                onClick={() => setViewingOrderId(order.id)}
              />
            </div>
              <div>
                <IconButton
                  icon={'pen'}
                  styles={{color: AppTheme.colors.themePurple, marginLeft: '.5rem'}}
                  size={'1x'}
                  iconToolTipText={'Edit'}
                  onClick={() => setEditingOrderData({editing: true, id: order.id})}
                />
              </div>
              <div>
                <IconButton
                  icon={'trash-alt'}
                  styles={{color: AppTheme.colors.danger, marginLeft: '.5rem'}}
                  size={'1x'}
                  iconToolTipText={'Permenantly delete order'}
                  onClick={() => setDeletingOrder(order)}
                />
              </div>
          </>
        )}
      </div>
    );
  }, [getOrderById]);

  const dateTimeFormatter = (isoDateTime: string) => {
    if (isNullOrWhitespace(isoDateTime)) return '';
    return format(utcToZonedTime(parseISO(isoDateTime), localTz as any as string), 'MM/dd/yyyy hh:mm:ss a', {timeZone: localTz as any as string});
  };


  const columns: MRT_ColumnDef<Order>[] = useMemo(() => [
    {
      id: 'organization',
      header: 'Organization',
      accessorFn: (row) => row.organization,
      enableSorting: true,
    },
    {
      id: 'submittedAt',
      header: `Submitted At (${localTzFriendlyFormat})`,
      accessorFn: (row) => dateTimeFormatter(row.submittedAt),
      enableSorting: true,
    },
    {
      id: 'storeItems',
      header: 'Store Items',
      accessorFn: (row) => orderStore.selectors.getTotalStoreItems({} as any)(row),
      enableSorting: true,
      filterVariant: 'text',
      filterFn: 'equals'
    },
    {
      id: 'warehouseItems',
      header: 'Warehouse Items',
      accessorFn: (row) => orderStore.selectors.getTotalWarehouseItems({} as any)(row),
      enableSorting: true,
      filterVariant: 'text',
      filterFn: 'equals'
    },
    {
      id: 'totalItems',
      header: 'Total Items',
      accessorFn: (row) => row.orderItems.reduce<number>((acc, oi) => acc + oi.storeQuantity + oi.warehouseQuantity + oi.itemOptions.reduce<number>((a, io) => a + io.quantity, 0), 0),
      enableSorting: true,
      filterVariant: 'text',
      filterFn: 'equals'
    },
    {
      id: 'uniqueItems',
      header: 'Unique Items',
      accessorFn: (row) => row.orderItems.length,
      enableSorting: true,
      filterVariant: 'text',
      filterFn: 'equals'
    },
    {
      id: 'totalCost',
      header: 'Total Cost',
      accessorKey: 'totalCost',
      Cell: ({ cell }) => formatCentsToUSD(cell.getValue<number>() ?? 0),
      enableSorting: true,
    },
    {
      id: 'actions',
      header: 'Actions',
      accessorFn: () => '',
      Cell: ({ row }) => actionsFormatter(null, row.original as Order),
      size: 0,
      minSize: 0,
      maxSize: 0,
    },
  ], [actionsFormatter]);

  return (
    <>
      {renderRedirect()}
      <Paper withBorder shadow="sm" radius="md" p="sm" className={gridStyles['responsive-table-wrapper']}>
        <StyledMantineTable
          columns={columns}
          data={deposits}
          printRef={printRef}
          initialState={{
            columnVisibility: {
              phoneNumber: false,
              address: false,
              approvedAt: false,
              shippedAt: false,
            },
            pagination: {pageSize: 15}
          }}
          topToolbarActions={
            <div className={gridStyles['button-group']}>
              <RoleProtectedComponent allowedRoles={['Administrator', 'Volunteer']}>
                <Button onClick={() => setCreatingOrder(makeOrder())} style={{ backgroundColor: AppTheme.colors.themePurple }}>
                  Deposit
                </Button>
              </RoleProtectedComponent>
              <Button onClick={handleprint} style={{ backgroundColor: AppTheme.colors.themePurple }}>
                Print Table
              </Button>
            </div>
          }
          mantineTableProps={{
            striped: true,
            highlightOnHover: true,
            withColumnBorders: true,
            className: gridStyles['table-auto'],
          }}
        />
      </Paper>

      {isDeleting !== '' && (
        <ConfirmationDialog
          onAccept={async () => {
            setIsDeleting('');
          }}
          onDecline={async () => {
            setIsDeleting('');
          }}
          open={isDeleting !== ''}
          prompt='Are you sure you want to remove this item?'
          positiveText='Yes'
          negativeText='No'
          positiveVariant='success'
          negativeVariant='danger'
        />
      )}

      {renderCancellationModal()}
      {renderDeletionModal()}

      {editingOrderData.editing && getOrderById(editingOrderData.id) ? (
        <DepositModal
          show={true}
          // If opened via URL param, default Deposit modal to read-only (view) mode
          readOnlyInitial={!!paramsId}
          onSubmit={() => {
            setEditingOrderData({editing: false, id: ''});
            if (paramsId) {
              navigate(RoutePaths.privatePaths.configurationPaths.depositManagement);
            }
          }}
          onCancel={() => {
            setEditingOrderData({editing: false, id: ''});
            if (paramsId) {
              navigate(RoutePaths.privatePaths.configurationPaths.depositManagement);
            }
          }}
          deposit={getOrderById(editingOrderData.id)!}
        />
      ) : null}

      {creatingOrder ? (
        <DepositModal
          show={true}
          onSubmit={() => setCreatingOrder(null)}
          onCancel={() => setCreatingOrder(null)}
          deposit={creatingOrder}
        />
      ) : null}

      {/* View-only modal when launched via the eye icon */}
      {viewingOrderId && getOrderById(viewingOrderId) ? (
        <DepositModal
          show={true}
          readOnlyInitial={true}
          onSubmit={() => setViewingOrderId('')}
          onCancel={() => setViewingOrderId('')}
          deposit={getOrderById(viewingOrderId)!}
        />
      ) : null}

      <AuditLogLoader
        opened={auditOpen}
        onClose={() => setAuditOpen(false)}
        entityId={auditEntityId}
      />
    </>
  );
}

const mapDispatchToProps = (dispatch: Dispatch) => ({actions: bindActionCreators({
    updateOrderStatus: orderStore.actions.updateOrderStatus,
    cancelOrder: orderStore.actions.cancelOrder,
    deleteOrder: orderStore.actions.deleteOrder
  }, dispatch)});
const mapStateToProps = (state: CommonState) => ({
  deposits: orderStore.selectors.deposits(state),
  getOrderById: orderStore.selectors.getById(state)
});
export default connect(mapStateToProps, mapDispatchToProps)(DepositGrid);
